import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Paper, Typography, Chip, Button, LinearProgress, Alert, Stack, Grid,
  IconButton, TextField, MenuItem, Drawer, Divider,
} from '@mui/material';
import {
  Science as ScienceIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import axios from 'axios';

// ─── Billing Queue (preview, read-only) ──────────────────────────────────────
// Mounted at /billing-queue. Shows Sage WRKORDER rows that look ready to bill,
// with a per-WO drawer for Eric + Pat to spot-check before we build the AI
// draft-invoice flow (Step 3 of Tom's SM billing plan).

const T = {
  fontStack: '-apple-system, BlinkMacSystemFont, "Inter", "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  text: '#0F172A', textMuted: '#64748B', textFaint: '#94A3B8',
  bg: '#F8FAFC', surface: '#FFFFFF', border: '#E2E8F0', borderSoft: '#EEF2F7',
  accent: '#0D2C54',
  shadowSm: '0 1px 2px rgba(15,23,42,0.04)',
  shadowMd: '0 1px 3px rgba(15,23,42,0.06), 0 4px 12px rgba(15,23,42,0.04)',
  radius: 12, radiusSm: 8,
  transition: 'all 150ms ease',
};

const URGENCY_STYLE = {
  urgent:  { dot: '#DC2626', text: '#991B1B', tint: '#FEF2F2', label: 'Urgent >90d' },
  overdue: { dot: '#EA580C', text: '#9A3412', tint: '#FFF7ED', label: 'Overdue >60d' },
  warn:    { dot: '#F59E0B', text: '#B45309', tint: '#FFFBEB', label: 'Aging >30d' },
  ok:      { dot: '#16A34A', text: '#15803D', tint: '#F0FDF4', label: 'Fresh' },
  unknown: { dot: '#94A3B8', text: '#64748B', tint: '#F1F5F9', label: '—' },
};

const money = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' }) : '—';

const StatCard = ({ label, value, sub, accent }) => (
  <Paper elevation={0} sx={{
    p: 2.5, borderRadius: `${T.radius}px`, border: `1px solid ${T.border}`, boxShadow: T.shadowSm,
    bgcolor: T.surface, display: 'flex', flexDirection: 'column', gap: 0.5,
  }}>
    <Typography sx={{ fontSize: 11, fontWeight: 700, color: T.textMuted, textTransform: 'uppercase', letterSpacing: 0.9 }}>
      {label}
    </Typography>
    <Typography sx={{ fontSize: 24, fontWeight: 600, color: accent || T.text, lineHeight: 1.1 }}>
      {value}
    </Typography>
    {sub && <Typography sx={{ fontSize: 11, color: T.textMuted }}>{sub}</Typography>}
  </Paper>
);

const UrgencyPill = ({ u }) => {
  const s = URGENCY_STYLE[u] || URGENCY_STYLE.unknown;
  return (
    <Box sx={{
      display: 'inline-flex', alignItems: 'center', gap: 0.5,
      px: 0.75, py: 0.1, borderRadius: 1,
      bgcolor: s.tint, color: s.text, border: `1px solid ${s.dot}22`,
      fontSize: 10.5, fontWeight: 700,
    }}>
      <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: s.dot }} />
      {s.label.toUpperCase()}
    </Box>
  );
};

const DetailDrawer = ({ wo, onClose }) => {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    if (!wo) return;
    setData(null); setErr(null);
    const token = localStorage.getItem('token');
    axios.get(`/api/billing-queue/${wo}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setData(r.data))
      .catch((e) => setErr(e.response?.data?.error || e.message));
  }, [wo]);

  return (
    <Drawer anchor="right" open={!!wo} onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100%', sm: 540 }, bgcolor: T.surface, fontFamily: T.fontStack } }}>
      <Box sx={{ p: 2.5, borderBottom: `1px solid ${T.borderSoft}`, display: 'flex', alignItems: 'center' }}>
        <Typography sx={{ fontSize: 16, fontWeight: 600, color: T.text, flex: 1 }}>
          WO #{wo}
        </Typography>
        <IconButton onClick={onClose} size="small"><CloseIcon fontSize="small" /></IconButton>
      </Box>
      {err && <Alert severity="error" sx={{ m: 2 }}>{err}</Alert>}
      {!data && !err && <Box sx={{ p: 4 }}><LinearProgress /></Box>}
      {data && (
        <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {/* Header */}
          <Box>
            <Typography sx={{ fontSize: 20, fontWeight: 600, color: T.text }}>{data.customer}</Typography>
            <Typography sx={{ fontSize: 13, color: T.textMuted, fontFamily: 'ui-monospace,monospace' }}>
              AR #{data.arcust} · Tech: {data.tech || '—'}
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap">
              <UrgencyPill u={data.urgency} />
              <Chip size="small" label={`Status ${data.status}`} sx={{ borderRadius: 1 }} />
              <Chip size="small" label={data.call_type} variant="outlined" sx={{ borderRadius: 1 }} />
              <Chip size="small" label={data.problem} variant="outlined" sx={{ borderRadius: 1 }} />
              {data.site.state === 'CT' && (
                <Chip size="small" label="CT · labor non-taxable" sx={{ borderRadius: 1, bgcolor: '#FEF3C7', color: '#92400E', fontWeight: 600 }} />
              )}
              {data.tax_exempt?.labor_exempt && (
                <Chip size="small" label="Labor tax-exempt"
                  sx={{ borderRadius: 1, bgcolor: '#DCFCE7', color: '#15803D', fontWeight: 600 }} />
              )}
              {data.tax_exempt?.material_exempt && (
                <Chip size="small" label="Material tax-exempt"
                  sx={{ borderRadius: 1, bgcolor: '#DCFCE7', color: '#15803D', fontWeight: 600 }} />
              )}
              {data.tax_exempt?.tax_group && (
                <Chip size="small" label={`Tax group ${data.tax_exempt.tax_group}`} variant="outlined"
                  sx={{ borderRadius: 1 }} />
              )}
            </Stack>
          </Box>

          <Divider />

          {/* Site */}
          <Box>
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: T.textMuted, textTransform: 'uppercase', letterSpacing: 0.9, mb: 0.5 }}>
              Site
            </Typography>
            <Typography sx={{ fontSize: 14, color: T.text }}>
              {data.site.address || '—'}
            </Typography>
            <Typography sx={{ fontSize: 13, color: T.textMuted }}>
              {[data.site.city, data.site.state, data.site.zip].filter(Boolean).join(', ')}
            </Typography>
          </Box>

          {/* Dates & $ */}
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: T.textMuted, textTransform: 'uppercase', letterSpacing: 0.9 }}>Entered</Typography>
              <Typography sx={{ fontSize: 14, color: T.text }}>{fmtDate(data.entered)} <span style={{ color: T.textMuted }}>· {data.age_days}d</span></Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: T.textMuted, textTransform: 'uppercase', letterSpacing: 0.9 }}>Service date</Typography>
              <Typography sx={{ fontSize: 14, color: T.text }}>{fmtDate(data.run_date || data.scheduled)}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: T.textMuted, textTransform: 'uppercase', letterSpacing: 0.9 }}>Line sale (items)</Typography>
              <Typography sx={{ fontSize: 14, color: T.text, fontWeight: 600 }}>
                {money(data.items.reduce((a, i) => a + i.total_sale, 0))}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: T.textMuted, textTransform: 'uppercase', letterSpacing: 0.9 }}>WO amount</Typography>
              <Typography sx={{ fontSize: 14, color: T.text, fontWeight: 600 }}>{money(data.wo_amt)}</Typography>
            </Grid>
          </Grid>

          {/* Items */}
          <Box>
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: T.textMuted, textTransform: 'uppercase', letterSpacing: 0.9, mb: 1 }}>
              Line items ({data.items.length})
            </Typography>
            {data.items.length === 0 ? (
              <Typography sx={{ fontSize: 13, color: T.textFaint, fontStyle: 'italic' }}>No line items yet.</Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                {data.items.map((i, idx) => (
                  <Box key={idx} sx={{
                    p: 1.25, borderRadius: `${T.radiusSm}px`, border: `1px solid ${T.borderSoft}`,
                    display: 'flex', gap: 1, alignItems: 'flex-start',
                  }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontSize: 13, fontWeight: 500, color: T.text,
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {i.description || i.itemnbr || '—'}
                      </Typography>
                      <Typography sx={{ fontSize: 11, color: T.textMuted }}>
                        {i.kind === 'labor' && i.labor_hours > 0 ? `${i.labor_hours}h` :
                         i.kind === 'parts' && i.quantity ? `qty ${i.quantity}` :
                         i.quantity ? `qty ${i.quantity}` : ''}
                        {(i.labor_hours > 0 || i.quantity > 0) && ' · '}
                        {money(i.unit_price)} unit · {i.kind} · {i.taxable ? 'taxable' : 'tax-exempt'}
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: T.text, whiteSpace: 'nowrap' }}>
                      {money(i.total_sale)}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Box>

          {/* Notes */}
          <Box>
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: T.textMuted, textTransform: 'uppercase', letterSpacing: 0.9, mb: 1 }}>
              Notes ({data.notes.length})
            </Typography>
            {data.notes.length === 0 && !data.header_comments ? (
              <Typography sx={{ fontSize: 13, color: T.textFaint, fontStyle: 'italic' }}>No notes.</Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {data.header_comments && (
                  <Box sx={{
                    p: 1.5, borderRadius: `${T.radiusSm}px`, bgcolor: '#F1F5F9',
                    border: `1px solid ${T.borderSoft}`,
                  }}>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: T.textMuted, mb: 0.5 }}>
                      HEADER COMMENTS
                    </Typography>
                    <Typography sx={{ fontSize: 13, color: T.text, whiteSpace: 'pre-wrap' }}>
                      {data.header_comments}
                    </Typography>
                  </Box>
                )}
                {data.notes.map((n) => (
                  <Box key={n.idx} sx={{
                    p: 1.5, borderRadius: `${T.radiusSm}px`,
                    bgcolor: n.customer_viewable ? '#F0FDF4' : T.surface,
                    border: `1px solid ${n.customer_viewable ? '#86EFAC' : T.borderSoft}`,
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                      <Typography sx={{ fontSize: 11, fontWeight: 700, color: T.text }}>
                        {n.title || `Note ${n.idx}`}
                      </Typography>
                      <Typography sx={{ fontSize: 11, color: T.textMuted }}>
                        · {fmtDate(n.date)} · {n.made_by || '—'}
                      </Typography>
                      <Box sx={{ flex: 1 }} />
                      {n.customer_viewable && (
                        <Chip size="small" label="Client-visible" sx={{ borderRadius: 1, bgcolor: '#DCFCE7', color: '#15803D', fontWeight: 600, height: 18, fontSize: 10 }} />
                      )}
                    </Box>
                    <Typography sx={{ fontSize: 13, color: T.text, whiteSpace: 'pre-wrap' }}>
                      {n.body}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Box>

          <Alert severity="info" icon={<ScienceIcon />} sx={{ borderRadius: `${T.radiusSm}px` }}>
            <strong>Preview build.</strong> Read-only. The AI draft-invoice flow lands after the rules meeting with Pat and Eric.
          </Alert>
        </Box>
      )}
    </Drawer>
  );
};

const BillingQueue = () => {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState('6');
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('age_desc');
  const [openWO, setOpenWO] = useState(null);

  const refresh = React.useCallback(() => {
    setLoading(true); setError(null);
    const token = localStorage.getItem('token');
    axios.get('/api/billing-queue', {
      headers: { Authorization: `Bearer ${token}` },
      params: { status, _: Date.now() },  // cache-bust — force fresh Sage pull
    })
      .then((r) => {
        setRows(r.data?.rows || []);
        setSummary(r.data?.summary || null);
      })
      .catch((e) => setError(e.response?.data?.error || e.message))
      .finally(() => setLoading(false));
  }, [status]);
  useEffect(() => { refresh(); }, [refresh]);

  // Auto-poll Sage every 60s while the page is open (per Tom 10/21).
  useEffect(() => {
    const id = setInterval(refresh, 60000);
    return () => clearInterval(id);
  }, [refresh]);

  const filtered = useMemo(() => {
    let r = rows;
    const q = search.trim().toLowerCase();
    if (q) {
      r = r.filter((x) =>
        String(x.wo).includes(q) ||
        x.customer.toLowerCase().includes(q) ||
        x.arcust.toLowerCase().includes(q) ||
        x.tech.toLowerCase().includes(q) ||
        x.site_city.toLowerCase().includes(q)
      );
    }
    const sorters = {
      age_desc: (a, b) => (b.age_days || 0) - (a.age_days || 0),
      age_asc:  (a, b) => (a.age_days || 0) - (b.age_days || 0),
      amt_desc: (a, b) => (b.line_sale || 0) - (a.line_sale || 0),
      amt_asc:  (a, b) => (a.line_sale || 0) - (b.line_sale || 0),
      cust:     (a, b) => a.customer.localeCompare(b.customer),
    };
    return [...r].sort(sorters[sortBy] || sorters.age_desc);
  }, [rows, search, sortBy]);

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, fontFamily: T.fontStack, bgcolor: T.bg, minHeight: '100vh', color: T.text }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
        <Box>
          <Typography sx={{ fontSize: 28, fontWeight: 600, letterSpacing: '-0.5px', color: T.text, lineHeight: 1.1 }}>
            Billing Queue
          </Typography>
          <Typography sx={{ fontSize: 13, color: T.textMuted, mt: 0.5 }}>
            Preview build · Sage Service Management · Read-only
          </Typography>
        </Box>
        <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
          <IconButton onClick={refresh} disabled={loading}
            sx={{ bgcolor: T.surface, border: `1px solid ${T.border}`, borderRadius: `${T.radiusSm}px` }}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Box>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: `${T.radiusSm}px` }}>{error}</Alert>}

      {summary && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} md={3}>
            <StatCard label="Open in queue" value={summary.total_wos} sub={`Status ${status}`} />
          </Grid>
          <Grid item xs={6} md={3}>
            <StatCard label="Potential invoice" value={money(summary.total_line_sale)} sub="sum of line sales" />
          </Grid>
          <Grid item xs={6} md={3}>
            <StatCard label="Urgent" value={summary.urgent_count} sub=">90 days old" accent="#991B1B" />
          </Grid>
          <Grid item xs={6} md={3}>
            <StatCard
              label="Billed today"
              value={summary.cleared_today?.billed?.n ?? 0}
              sub={`${money(summary.cleared_today?.billed?.amt ?? 0)}${summary.cleared_today?.voided?.n ? ` · ${summary.cleared_today.voided.n} voided` : ''}`}
              accent="#15803D" />
          </Grid>
        </Grid>
      )}

      {/* Filters */}
      <Paper elevation={0} sx={{
        p: 2, mb: 2.5, borderRadius: `${T.radius}px`, bgcolor: T.surface,
        border: `1px solid ${T.border}`, boxShadow: T.shadowSm,
        display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap',
      }}>
        <TextField size="small" select label="Status" value={status} onChange={(e) => setStatus(e.target.value)}
          sx={{ minWidth: 150 }}>
          <MenuItem value="6">6 — Ready to bill</MenuItem>
          <MenuItem value="0">0 — Open</MenuItem>
          <MenuItem value="2">2 — In progress</MenuItem>
          <MenuItem value="0,2,6">All open (0, 2, 6)</MenuItem>
        </TextField>
        <TextField size="small" select label="Sort" value={sortBy} onChange={(e) => setSortBy(e.target.value)}
          sx={{ minWidth: 170 }}>
          <MenuItem value="age_desc">Oldest first</MenuItem>
          <MenuItem value="age_asc">Newest first</MenuItem>
          <MenuItem value="amt_desc">$ highest first</MenuItem>
          <MenuItem value="amt_asc">$ lowest first</MenuItem>
          <MenuItem value="cust">Customer A→Z</MenuItem>
        </TextField>
        <TextField size="small" placeholder="Search WO# / client / tech / city" value={search}
          onChange={(e) => setSearch(e.target.value)} sx={{ minWidth: 280, flex: 1 }} />
        <Typography sx={{ fontSize: 12, color: T.textMuted }}>
          {filtered.length} shown
        </Typography>
      </Paper>

      {loading && <LinearProgress sx={{ mb: 2, borderRadius: 1, height: 2 }} />}

      {/* Table */}
      <Paper elevation={0} sx={{
        borderRadius: `${T.radius}px`, bgcolor: T.surface,
        border: `1px solid ${T.border}`, boxShadow: T.shadowMd, overflow: 'hidden',
      }}>
        <Box sx={{ overflow: 'auto' }}>
          <Box sx={{ display: 'grid',
            gridTemplateColumns: '110px 90px 1fr 160px 110px 120px 110px 90px',
            minWidth: 1100,
            bgcolor: T.surface,
            borderBottom: `1px solid ${T.borderSoft}`,
            fontSize: 11, fontWeight: 700, color: T.textFaint, textTransform: 'uppercase', letterSpacing: 0.9,
          }}>
            {['WO #', 'Status', 'Customer', 'Tech', 'Age', 'Line Sale', 'Call Type', 'City'].map((h, i) => (
              <Box key={i} sx={{ px: 2, py: 1.5 }}>{h}</Box>
            ))}
          </Box>
          {filtered.map((r, idx) => (
            <Box key={r.wo}
              onClick={() => setOpenWO(r.wo)}
              sx={{
                display: 'grid',
                gridTemplateColumns: '110px 90px 1fr 160px 110px 120px 110px 90px',
                minWidth: 1100,
                borderBottom: `1px solid ${T.borderSoft}`,
                bgcolor: idx % 2 ? '#FBFCFE' : T.surface,
                cursor: 'pointer',
                transition: T.transition,
                '&:hover': { bgcolor: '#F1F5FB' },
                alignItems: 'center',
                fontSize: 13,
              }}>
              <Box sx={{ px: 2, py: 1.5, fontFamily: 'ui-monospace,monospace', fontWeight: 600, color: T.accent, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                #{r.wo}
                <OpenInNewIcon sx={{ fontSize: 12, color: T.textFaint, opacity: 0, transition: T.transition, 'tr:hover > &': { opacity: 1 } }} />
              </Box>
              <Box sx={{ px: 2, py: 1.5 }}>
                <UrgencyPill u={r.urgency} />
              </Box>
              <Box sx={{ px: 2, py: 1.5 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 500, color: T.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {r.customer || '—'}
                </Typography>
                <Typography sx={{ fontSize: 11, color: T.textMuted, fontFamily: 'ui-monospace,monospace' }}>
                  AR #{r.arcust || '—'} · {r.problem}
                </Typography>
              </Box>
              <Box sx={{ px: 2, py: 1.5, color: T.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {r.tech || '—'}
              </Box>
              <Box sx={{ px: 2, py: 1.5, color: T.textMuted }}>
                {r.age_days != null ? `${r.age_days}d` : '—'}
              </Box>
              <Box sx={{ px: 2, py: 1.5, fontWeight: 600, color: T.text }}>
                {money(r.line_sale)}
              </Box>
              <Box sx={{ px: 2, py: 1.5, color: T.textMuted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: 12 }}>
                {r.call_type}
              </Box>
              <Box sx={{ px: 2, py: 1.5, color: T.textMuted, fontSize: 12 }}>
                {r.site_city || '—'}{r.site_state ? ` ${r.site_state}` : ''}
              </Box>
            </Box>
          ))}
          {!loading && filtered.length === 0 && (
            <Box sx={{ p: 6, textAlign: 'center' }}>
              <Typography sx={{ color: T.textMuted }}>No WOs match the current filters.</Typography>
            </Box>
          )}
        </Box>
      </Paper>

      <Alert severity="info" icon={<ScienceIcon />} sx={{ mt: 2.5, borderRadius: `${T.radiusSm}px` }}>
        <strong>Preview build.</strong> Read-only Sage view. The AI draft-invoice flow lands after the SM billing rules meeting with Pat Kross and Eric Olivier.
      </Alert>

      <DetailDrawer wo={openWO} onClose={() => setOpenWO(null)} />
    </Box>
  );
};

export default BillingQueue;
